import { useColorMode } from '@chakra-ui/react';
import { createContext, FC, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import { useFeatureFlag } from '@bonusly/who-am-i';

import { useLocalStorage } from '../../lib/storage/hooks/use-local-storage';
import { getActiveSeasonConfig } from './seasonal-config';

interface SeasonalThemeContextValue {
	/** True if the user has the feature flag enabled (can see and use seasonal themes) */
	hasSeasonalThemePermission: boolean;
	/** True if user has permission AND has toggled the theme on */
	isSeasonalThemeEnabled: boolean;
	toggleTheme: () => void;
}

export const SeasonalThemeContext = createContext<SeasonalThemeContextValue | undefined>(undefined);

export const SeasonalThemeProvider: FC<PropsWithChildren> = ({ children }) => {
	const hasSeasonalThemesFlag = useFeatureFlag('seasonal-themes');
	const activeSeasonConfig = getActiveSeasonConfig();
	const [userPreference, setUserPreference] = useLocalStorage<boolean>(activeSeasonConfig.storageKey, false);
	const { colorMode, setColorMode } = useColorMode();

	// Store the color mode before seasonal theme was enabled so we can restore it
	const previousColorModeRef = useRef<'light' | 'dark'>(colorMode);

	const isSeasonalThemeEnabled = Boolean(hasSeasonalThemesFlag && userPreference);

	// Handle color mode enforcement: if seasonal theme is enabled → force dark mode
	useEffect(() => {
		if (isSeasonalThemeEnabled) {
			// Store current color mode before switching to dark
			if (colorMode !== 'dark') {
				previousColorModeRef.current = colorMode;
			}
			setColorMode('dark');
		}
	}, [isSeasonalThemeEnabled, colorMode, setColorMode]);

	// Handle feature flag removal: clear stale seasonal preference
	useEffect(() => {
		if (!hasSeasonalThemesFlag && userPreference) {
			setUserPreference(false);
		}
	}, [hasSeasonalThemesFlag, userPreference, setUserPreference]);

	const toggleTheme = useCallback(() => {
		const newValue = !userPreference;
		setUserPreference(newValue);

		// If turning off seasonal theme, restore the previous color mode
		if (!newValue && previousColorModeRef.current === 'light') {
			setColorMode('light');
		}
	}, [userPreference, setUserPreference, setColorMode]);

	const contextValue: SeasonalThemeContextValue = useMemo(
		() => ({
			// User has permission if feature flag is enabled
			hasSeasonalThemePermission: Boolean(hasSeasonalThemesFlag),
			// Theme is enabled only if user has permission AND has chosen to enable it
			isSeasonalThemeEnabled,
			toggleTheme,
		}),
		[hasSeasonalThemesFlag, isSeasonalThemeEnabled, toggleTheme]
	);

	return <SeasonalThemeContext.Provider value={contextValue}>{children}</SeasonalThemeContext.Provider>;
};

/**
 * Hook to access seasonal theme state and controls
 *
 * Returns safe defaults when used outside of SeasonalThemeProvider:
 * - hasSeasonalThemePermission: false
 * - isSeasonalThemeEnabled: false
 * - toggleTheme: no-op function
 *
 * This allows components to gracefully degrade in contexts without the provider.
 */
export const useSeasonalTheme = () => {
	const context = useContext(SeasonalThemeContext);

	// Gracefully handle usage outside of provider
	if (!context) {
		return {
			hasSeasonalThemePermission: false,
			isSeasonalThemeEnabled: false,
			toggleTheme: () => {},
		};
	}

	return context;
};
